/**
 * Estados de falha de integração de ocorrência na grade de Posição de Notas
 * Fiscais (TMS-8396 fase 2 — ADR-0002, decisão D1).
 *
 * Funções puras, sem dependência de Vue e sem biblioteca de terceiros. É essa
 * pureza que dá a esta frente a única evidência automatizável possível num
 * repositório sem runner de teste: a tabela-verdade em
 * `tools/verificar-estados-ocorrencia.mjs`.
 *
 * A extensão `.js` do import abaixo é OBRIGATÓRIA: o harness espelha este módulo
 * num diretório temporário `{"type":"module"}` e o resolvedor ESM do Node não
 * adivinha extensão. O webpack resolve com ou sem — o custo é zero.
 *
 * Precedente de produção desta forma de extrair regra de view legada:
 * `src/utils/expedicaoRowVisibility.js`.
 */

import {
  ACAO_ABRIR_CHAMADO,
  ACAO_REENVIAR,
  CHAMADO_DETALHES_TITULO,
  CHAMADO_TITULO,
  E1_CORPO,
  E1_CORPO_SEM_DATA,
  E1_CORPO_SEM_MOTIVO,
  E2_CORPO,
  E3_CORPO,
  E4A_CORPO,
  E4A_CORPO_SEM_DATA,
  E4B_CORPO,
  REENVIO_INDISPONIVEL_MOTIVO_AUSENTE,
  REENVIO_INDISPONIVEL_PREFIXO,
  REENVIO_INDISPONIVEL_SUFIXO,
  ROTULO_CARGA,
  ROTULO_CODIGO_OCORRENCIA,
  ROTULO_HORARIO_OCORRENCIA,
  ROTULO_HORARIO_ULTIMA_TENTATIVA,
  ROTULO_ID_OCORRENCIA,
  ROTULO_MOTIVO,
  ROTULO_NOTA,
  ROTULO_NUMERO_TENTATIVA,
  ROTULO_NUMERO_TENTATIVAS,
  ROTULO_RESPOSTA_ITRACK,
  ROTULO_TIPO_FALHA,
  TITULOS_POR_ESTADO,
  VALOR_AUSENTE,
} from "../constants/ocorrenciaIntegracaoCopy.js";

/** `status_integracao` da ocorrência: 1 Enviado, 2 Pendente, 3 Erro. */
const STATUS_INTEGRACAO_ERRO = 3;

export const ESTADOS = Object.freeze({
  E1: "E1",
  E2: "E2",
  E3: "E3",
  E4A: "E4a",
  E4B: "E4b",
});

function temValor(valor) {
  return valor !== null && valor !== undefined && String(valor).trim() !== "";
}

function texto(valor) {
  return temValor(valor) ? String(valor).trim() : "";
}

/* -------------------------------------------------------------------------- */
/* Pontuação da junção motivo + copy                                           */
/* -------------------------------------------------------------------------- */

/**
 * Pontuação de fim de frase, incluindo espaço em branco à direita.
 *
 * `)` NÃO entra: `'Falha de comunicação com o rastreamento (iTrack)'` termina em
 * parêntese e ele é parte do texto aprovado.
 */
const PONTUACAO_FINAL = /[\s.,;:!?…]+$/;

/**
 * Motivo sem a pontuação final — para quando a copy que vem depois já abre com o
 * ponto.
 *
 * O backend não garante pontuação nenhuma: `TpFalhaIntegracaoEnum::mensagemOperacao()`
 * devolve as cinco frases SEM ponto ('Unidade emissora não cadastrada'), enquanto
 * `motivoIntegracao` é resposta crua da parceira e chega como ela mandar. Sem esta
 * aparagem, o motivo com ponto encostado no sufixo `". Reenviar…"` rendia o ponto
 * duplo `"…não foi resolvida.. Reenviar…"`.
 *
 * Motivo que seja SÓ pontuação vira string vazia, e quem chama cai no fallback —
 * é o comportamento certo: não há motivo a exibir.
 *
 * @param {*} valor
 * @returns {string}
 */
function semPontuacaoFinal(valor) {
  return texto(valor).replace(PONTUACAO_FINAL, "");
}

/**
 * Motivo terminado em ponto — para quando a copy que vem depois começa uma frase
 * nova sem trazer o ponto.
 *
 * É o caso de `E2_CORPO` (`"{motivoIntegracaoLabel} Reenviar não resolve…"`), que
 * reproduz literalmente a copy aprovada `"<motivo>. Reenviar nao resolve…"`: o
 * ponto pertence à junção, não à constante. Com o motivo real de
 * `mensagemOperacao()`, que não tem ponto, a frase saía emendada.
 *
 * @param {*} valor
 * @returns {string}
 */
function comPontuacaoFinal(valor) {
  const limpo = semPontuacaoFinal(valor);

  return limpo === "" ? "" : `${limpo}.`;
}

/**
 * Substitui `{chave}` pelos valores informados. Chave sem valor vira string
 * vazia — os modelos desta feature nunca dependem disso: quando um placeholder
 * não tem valor, quem escolhe é `corpoDoEstado`, trocando o modelo inteiro por
 * uma variante literal.
 */
function interpolar(modelo, valores) {
  return modelo.replace(/\{(\w+)\}/g, (_, chave) =>
    Object.prototype.hasOwnProperty.call(valores, chave)
      ? String(valores[chave])
      : ""
  );
}

/**
 * `"YYYY-MM-DD HH:mm:ss"` (o que `CargaOcorrenciaResource` serializa via
 * `->toDateTimeString()`) ou ISO-8601 com `T` → `"DD/MM às HH:mm"`.
 *
 * Qualquer outra coisa, ou nulo, devolve `null`, e a frase que usaria a data é
 * omitida. Sem `moment`: o harness roda fora do `node_modules` do repositório.
 *
 * @param {string|null|undefined} valor
 * @returns {string|null}
 */
export function formatarDataHora(valor) {
  if (typeof valor !== "string") {
    return null;
  }

  const partes = valor
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2})?/);

  if (!partes) {
    return null;
  }

  const mes = Number(partes[2]);
  const dia = Number(partes[3]);
  const hora = Number(partes[4]);
  const minuto = Number(partes[5]);

  const foraDeFaixa =
    mes < 1 || mes > 12 || dia < 1 || dia > 31 || hora > 23 || minuto > 59;

  return foraDeFaixa ? null : `${partes[3]}/${partes[2]} às ${partes[4]}:${partes[5]}`;
}

/**
 * Resolve o estado de erro de integração da ocorrência.
 *
 * A ordem de avaliação é parte da decisão (ADR-0002 D1) — NÃO reordene.
 *
 * ## A assimetria das duas guardas — leia antes de "uniformizar"
 *
 * O **botão** de reenvio segue `reenvioPermitido === false` (fail-open), idêntico
 * ao que já está em produção em `InclusaoOcorrenciaDetalhes.vue:150-186` e em
 * `expedicaoRowVisibility.js:101-113`, porque o bloqueio autoritativo é o **422 do
 * endpoint**, não a tela. Ver `isReenvioBloqueado` abaixo.
 *
 * O **bloco de copy** exige prova positiva: sem `typeof retentativaAutomatica
 * === "boolean"`, esta função devolve `null` e **nada renderiza**.
 *
 * As duas guardas são deliberadamente diferentes porque os custos do erro são
 * diferentes: um botão oferecido a mais custa um POST que o 422 recusa; uma frase
 * falsa na tela custa o chamado que esta task existe para eliminar — a copy de E1
 * e E4 afirma fatos ("o iSend vai tentar de novo sozinho", "tentou reenviar N
 * vezes").
 *
 * Efeito colateral que sustenta a ordem de deploy: sem as chaves novas no
 * payload, a tela fica exatamente como hoje — o front pode subir antes do
 * backend. Quem uniformizar as duas guardas faz as 627.880 ocorrências de E3
 * exibirem copy de E4, com frases falsas (risco A7F-R02).
 *
 * ## Regra dura herdada da fase 1 — não negociável
 *
 * O front NUNCA deriva "reenvio automático em andamento" comparando
 * `tentativasIntegracao` com um teto. O teto é variável de ambiente do backend
 * (`EAGLE_RETENTATIVA_TENTATIVAS_MAXIMAS`) e o front não o conhece. O único
 * discriminante é `retentativaAutomatica`. `tentativasIntegracao` só serve para
 * escolher entre E4a e E4b e para compor o detalhe técnico.
 *
 * @param {object} item ocorrência serializada por `CargaOcorrenciaResource` (camelCase)
 * @returns {"E1"|"E2"|"E3"|"E4a"|"E4b"|null} `null` é o gate E0: não renderizar nada
 */
export function resolverEstadoIntegracao(item) {
  /* 1. Gate E0 — só ocorrência em erro tem bloco de diagnóstico. */
  if (
    !item ||
    !item.statusIntegracao ||
    item.statusIntegracao.valor != STATUS_INTEGRACAO_ERRO
  ) {
    return null;
  }

  /* 2. Gate de contrato — prova positiva, não veracidade. */
  if (typeof item.retentativaAutomatica !== "boolean") {
    return null;
  }

  /* 3. A retentativa automática está agendada. */
  if (item.retentativaAutomatica === true) {
    return ESTADOS.E1;
  }

  /* 4. Backend ainda permite reenvio — mesma leitura fail-open do botão. */
  if (item.reenvioPermitido !== false) {
    return temValor(item.tentativasIntegracao) ? ESTADOS.E4A : ESTADOS.E4B;
  }

  /* 5. Falha permanente com diagnóstico preservado. */
  if (temValor(item.motivoIntegracaoLabel)) {
    return ESTADOS.E2;
  }

  /* 6. Falha permanente anterior ao diagnóstico automático (TMS-7218). */
  return ESTADOS.E3;
}

/**
 * Estado da ação "Reenviar" — fail-open, no molde de
 * `expedicaoRowVisibility.js:107-113`.
 *
 * A guarda é `=== false`, jamais `!item.reenvioPermitido`: chave ausente é o
 * payload de toda a base anterior ao deploy, e com guarda por veracidade
 * "ausente" viraria "bloqueado", escondendo um botão que o backend permite. Sem a
 * chave, o 422 do endpoint segue sendo o bloqueio autoritativo.
 *
 * Esta é a guarda que NÃO pode ser uniformizada com o gate de contrato de
 * `resolverEstadoIntegracao` — ver o JSDoc de lá.
 *
 * @param {object} item
 * @returns {boolean}
 */
export function isReenvioBloqueado(item) {
  return Boolean(item && item.reenvioPermitido === false);
}

/**
 * Título aprovado do estado.
 *
 * O acesso é por `hasOwnProperty` e não por indexação direta: `estado` pode
 * chegar de dado, e indexar o objeto aceitaria chaves herdadas do protótipo.
 *
 * @param {string} estado
 * @returns {string|null}
 */
export function tituloDoEstado(estado) {
  return Object.prototype.hasOwnProperty.call(TITULOS_POR_ESTADO, estado)
    ? TITULOS_POR_ESTADO[estado]
    : null;
}

/**
 * Corpo aprovado do estado, com os placeholders já interpolados.
 *
 * Quando um placeholder não tem valor, a variante literal correspondente é usada
 * — a oração cai inteira e o resto do texto permanece palavra por palavra. Nunca
 * se imprime `null`, e nunca se inventa texto para tapar o buraco.
 *
 * @param {string} estado
 * @param {object} item
 * @returns {string|null}
 */
export function corpoDoEstado(estado, item) {
  const ocorrencia = item || {};
  const motivo = semPontuacaoFinal(ocorrencia.motivoIntegracaoLabel);
  const data = formatarDataHora(ocorrencia.dtUltimaTentativaIntegracao);

  if (estado === ESTADOS.E1) {
    if (!motivo) {
      return E1_CORPO_SEM_MOTIVO;
    }
    /* Os dois modelos de E1 já trazem o ponto depois do motivo (`{motivo}. O
       iSend…`) ou o encostam em ` em {data}.` — em ambos, o motivo entra sem
       pontuação final. */
    return interpolar(data ? E1_CORPO : E1_CORPO_SEM_DATA, {
      motivoIntegracaoLabel: motivo,
      dtUltimaTentativaIntegracao: data,
    });
  }

  if (estado === ESTADOS.E2) {
    /* E2 é o inverso: a segunda frase da copy aprovada começa direto em
       "Reenviar", sem o ponto. Ele entra aqui, na junção. */
    return interpolar(E2_CORPO, {
      motivoIntegracaoLabel: comPontuacaoFinal(motivo),
    });
  }

  if (estado === ESTADOS.E3) {
    return E3_CORPO;
  }

  if (estado === ESTADOS.E4A) {
    return interpolar(data ? E4A_CORPO : E4A_CORPO_SEM_DATA, {
      tentativasIntegracao: ocorrencia.tentativasIntegracao,
      dtUltimaTentativaIntegracao: data,
    });
  }

  if (estado === ESTADOS.E4B) {
    return E4B_CORPO;
  }

  return null;
}

function codigoDaOcorrencia(item) {
  const param = item.paramOcorrenciaResource;

  if (!param) {
    return "";
  }

  return [texto(param.cd), texto(param.descr)].filter(Boolean).join(" - ");
}

function numeroDaCarga(item) {
  return item.carga ? texto(item.carga.numCarga) : "";
}

function numeroDaNota(item) {
  const doc = item.doc;
  const numFiscal = doc ? texto(doc.numFiscal) : "";

  return numFiscal || texto(item.numControle);
}

/**
 * Motivo legível da falha. `motivoIntegracao` é resposta crua da parceira e entra
 * apenas como segunda opção — renderize sempre por `{{ }}`, nunca por `v-html`,
 * inclusive no texto copiado.
 */
function motivoDaFalha(item) {
  return texto(item.motivoIntegracaoLabel) || texto(item.motivoIntegracao);
}

function detalhe(rotulo, valor) {
  return temValor(valor) ? [{ rotulo, valor: String(valor).trim() }] : [];
}

/**
 * Lista rótulo/valor do bloco recolhido "Detalhes técnicos".
 *
 * E3 devolve lista vazia por definição: `tpFalhaIntegracao` e `motivoIntegracao`
 * são nulos nesse estado, e o bloco nasceria vazio (ADR-0002 D6).
 *
 * @param {string} estado
 * @param {object} item
 * @returns {Array<{rotulo: string, valor: string}>}
 */
export function detalhesTecnicos(estado, item) {
  const ocorrencia = item || {};

  const identificacao = [
    ...detalhe(ROTULO_CODIGO_OCORRENCIA, codigoDaOcorrencia(ocorrencia)),
    ...detalhe(ROTULO_ID_OCORRENCIA, ocorrencia.carocorrenId),
    ...detalhe(ROTULO_CARGA, numeroDaCarga(ocorrencia)),
  ];

  if (estado === ESTADOS.E1) {
    return [
      ...identificacao,
      ...detalhe(
        ROTULO_HORARIO_ULTIMA_TENTATIVA,
        formatarDataHora(ocorrencia.dtUltimaTentativaIntegracao)
      ),
      ...detalhe(ROTULO_NUMERO_TENTATIVA, ocorrencia.tentativasIntegracao),
      ...detalhe(ROTULO_RESPOSTA_ITRACK, ocorrencia.motivoIntegracao),
    ];
  }

  if (estado === ESTADOS.E2) {
    return [
      ...identificacao,
      ...detalhe(
        ROTULO_HORARIO_OCORRENCIA,
        formatarDataHora(ocorrencia.dtOcorrencia)
      ),
      ...detalhe(ROTULO_TIPO_FALHA, ocorrencia.tpFalhaIntegracao),
    ];
  }

  if (estado === ESTADOS.E4A || estado === ESTADOS.E4B) {
    return [
      ...identificacao,
      ...detalhe(ROTULO_NUMERO_TENTATIVAS, ocorrencia.tentativasIntegracao),
      ...detalhe(ROTULO_RESPOSTA_ITRACK, ocorrencia.motivoIntegracao),
    ];
  }

  return [];
}

/**
 * Bloco que vai para a área de transferência quando o operador aciona "Abrir
 * chamado com estes dados" — já com carga, nota, ocorrência e motivo
 * preenchidos, que são os quatro itens que a copy aprovada exige.
 *
 * Não existe integração com Service Desk neste repositório (ADR-0002 D5): a ação
 * copia e instrui a colagem. A técnica é a do precedente da squad em
 * `Expedicao.vue` (TMS-7247) — copiada, nunca importada, porque aquele arquivo
 * não está em `production`.
 *
 * @param {object} item
 * @returns {string}
 */
export function textoParaChamado(item) {
  if (!item) {
    return "";
  }

  const estado = resolverEstadoIntegracao(item);

  const linhas = [
    CHAMADO_TITULO,
    "",
    `${ROTULO_CARGA}: ${numeroDaCarga(item) || VALOR_AUSENTE}`,
    `${ROTULO_NOTA}: ${numeroDaNota(item) || VALOR_AUSENTE}`,
    `${ROTULO_CODIGO_OCORRENCIA}: ${codigoDaOcorrencia(item) || VALOR_AUSENTE}`,
    `${ROTULO_MOTIVO}: ${motivoDaFalha(item) || VALOR_AUSENTE}`,
  ];

  const detalhes = detalhesTecnicos(estado, item);

  if (detalhes.length > 0) {
    linhas.push("", CHAMADO_DETALHES_TITULO);
    detalhes.forEach((linha) => {
      linhas.push(`- ${linha.rotulo}: ${linha.valor}`);
    });
  }

  return linhas.join("\n");
}

/**
 * Tooltip do botão "Reenviar" desabilitado. Porte camelCase de
 * `InclusaoOcorrenciaDetalhes.vue:457-468` — o texto é reusado, não reescrito, e
 * o fallback é o mesmo de `:461`.
 *
 * `REENVIO_INDISPONIVEL_SUFIXO` abre em `". "`, então o motivo entra aparado. O
 * porte legado não aparava e por isso um motivo terminado em ponto rendia
 * `"…não foi resolvida.. Reenviar…"`. O fallback nunca expôs o defeito porque
 * `REENVIO_INDISPONIVEL_MOTIVO_AUSENTE` não termina em ponto.
 *
 * @param {object} item
 * @returns {string}
 */
export function tituloReenvioIndisponivel(item) {
  const motivo =
    semPontuacaoFinal(item && motivoDaFalha(item)) ||
    REENVIO_INDISPONIVEL_MOTIVO_AUSENTE;

  return REENVIO_INDISPONIVEL_PREFIXO + motivo + REENVIO_INDISPONIVEL_SUFIXO;
}

/* -------------------------------------------------------------------------- */
/* Forma do bloco por estado (TMS-8396-FE-02)                                  */
/* -------------------------------------------------------------------------- */

/**
 * Chaves das duas ações. O componente decide o que renderizar por esta chave e
 * não pelo rótulo: comparar texto de tela quebraria em silêncio no dia em que a
 * copy for revisada.
 */
export const ACOES = Object.freeze({
  REENVIAR: "reenviar",
  ABRIR_CHAMADO: "abrirChamado",
});

function descritorDeAcao(chave, rotulo, habilitada) {
  return Object.freeze({ chave, rotulo, habilitada });
}

const REENVIAR_HABILITADO = descritorDeAcao(ACOES.REENVIAR, ACAO_REENVIAR, true);
const REENVIAR_DESABILITADO = descritorDeAcao(
  ACOES.REENVIAR,
  ACAO_REENVIAR,
  false
);
const ABRIR_CHAMADO = descritorDeAcao(
  ACOES.ABRIR_CHAMADO,
  ACAO_ABRIR_CHAMADO,
  true
);

/** E3 e estado desconhecido: badge e texto neutro, sem ação e sem detalhes. */
const SEM_ACAO = Object.freeze({
  primaria: null,
  secundaria: null,
  detalhesTecnicos: false,
});

const ACOES_POR_ESTADO = Object.freeze({
  [ESTADOS.E1]: Object.freeze({
    primaria: REENVIAR_HABILITADO,
    secundaria: null,
    detalhesTecnicos: true,
  }),
  [ESTADOS.E2]: Object.freeze({
    primaria: ABRIR_CHAMADO,
    secundaria: REENVIAR_DESABILITADO,
    detalhesTecnicos: true,
  }),
  [ESTADOS.E3]: SEM_ACAO,
  [ESTADOS.E4A]: Object.freeze({
    primaria: REENVIAR_HABILITADO,
    secundaria: ABRIR_CHAMADO,
    detalhesTecnicos: true,
  }),
  [ESTADOS.E4B]: Object.freeze({
    primaria: REENVIAR_HABILITADO,
    secundaria: ABRIR_CHAMADO,
    detalhesTecnicos: true,
  }),
});

/**
 * Forma do bloco de cada estado: qual ação é primária, qual é secundária e se há
 * bloco recolhido de "Detalhes técnicos".
 *
 * É a tabela do plano da FE-02 exposta como função pura, para que o harness
 * `tools/verificar-estados-ocorrencia.mjs` a exerça — num repositório sem runner
 * de componente, esta é a única forma de ter evidência automatizável da forma do
 * bloco, e não só do texto.
 *
 * ## O que esta tabela NÃO decide
 *
 * `primaria.habilitada` é a forma aprovada do estado, não a permissão da
 * instância. O componente compõe dois outros fatores antes de renderizar o botão
 * de reenvio:
 *   - a prop `podeReenviar` (permissão de interface, resolvida pela view);
 *   - `isReenvioBloqueado(item)` — a guarda fail-open do botão.
 *
 * A composição importa num caso real: E1 com `reenvioPermitido = false` existe
 * (retentativa agendada e reenvio manual vedado). A tabela diz "Reenviar
 * habilitada"; a guarda diz que não. Quem manda é a guarda, e o botão sai inerte
 * com o tooltip — jamais o contrário.
 *
 * O acesso é por `hasOwnProperty`, como em `tituloDoEstado`: `estado` pode chegar
 * de dado e indexar o objeto aceitaria chaves herdadas do protótipo.
 *
 * @param {string} estado
 * @returns {{primaria: object|null, secundaria: object|null, detalhesTecnicos: boolean}}
 */
export function acoesDoEstado(estado) {
  return Object.prototype.hasOwnProperty.call(ACOES_POR_ESTADO, estado)
    ? ACOES_POR_ESTADO[estado]
    : SEM_ACAO;
}

/** Rótulos das ações, reexportados para a view não importar dois módulos. */
export { ACAO_ABRIR_CHAMADO, ACAO_REENVIAR };
